import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Schemas } from '@/api/client';
import { plansQuery, qk } from '@/api/queries';
import { num } from '@/api/types';
import { useSession } from '@/app/session';
import { Button, Card, FieldHint, Input, Modal, ModalFooter, ModalHeader, Progress, Segmented, useModalTitleId, useToast } from '@/design-system';
import { formatNumber, formatSum } from '@/lib/format';
import { monthly, pct, planName } from '@/lib/subscriptions';
import { errorText } from '@/shell/errorText';
import { assignPlan, patchSubscription, type Subscription } from './api';
import { useOrg } from './OrganizationPage';
import s from './organizations.module.css';

const LIMITS = ['generations_per_month', 'demo_sites', 'storage_gb', 'production_stores'] as const;
const PAYMENT_TONE: Record<string, string> = { paid: s.chipOk ?? '', awaiting: s.chipAcc ?? '', overdue: s.chipErr ?? '', unknown: s.chipMute ?? '' };

/** «01.03.2026». */
function fullDate(d: string): string {
  const [y, m, day] = d.slice(0, 10).split('-');
  return `${day}.${m}.${y}`;
}

/** Подписка (DESIGN_SPEC §4.3): the price card and the plan limits; changing it is additive (P1-F02). */
export function SubscriptionTab() {
  const { org, subscription: sub } = useOrg();
  const { t } = useTranslation('organizations');
  const { t: tc } = useTranslation('common');
  const { can } = useSession();
  const [editing, setEditing] = useState(false);
  const canChange = can('subscription:write');

  if (!sub) {
    return (
      <>
        <Card className={s.pending}>
          <span className={s.pendingTitle}>{t('subscription.none_title')}</span>
          <span className={s.pendingText}>{t('subscription.none_text')}</span>
        </Card>
        {canChange && (
          <Button size={38} icon="plus" className={s.alignStart} onClick={() => setEditing(true)}>
            {t('subscription.assign')}
          </Button>
        )}
        {editing && <SubscriptionModal orgId={org.id} sub={null} onClose={() => setEditing(false)} />}
      </>
    );
  }

  const period = sub.period as 'yearly' | 'monthly' | 'one_time';
  const listMonthly = monthly(sub.price, sub.period);
  const effMonthly = monthly(sub.effective_price, sub.period);
  const limits = (sub.plan_snapshot.limits ?? {}) as Record<string, number | null | undefined>;
  const usage = sub.usage ?? {};
  const discount = sub.discount_percent
    ? [t('subscription.discount', { pct: pct(sub.discount_percent) }), sub.contract_ref ? t('subscription.contract', { ref: sub.contract_ref }) : sub.discount_reason].filter(Boolean).join(' · ')
    : null;
  return (
    <>
      <div className={s.subGrid}>
        <Card padding={24} className={s.priceCard}>
          <div className={s.titles3}>
            <span className={s.eyebrow12}>{t('subscription.eyebrow', { period: t(`subscription.period.${period}`) })}</span>
            <span className={s.planName}>{planName(sub)}</span>
          </div>
          <div className={s.priceBlock}>
            <div className={s.priceRow}>
              <span className={s.price}>{formatSum(effMonthly)}</span>
              <span className={s.perMonth}>{t('subscription.per_month')}</span>
            </div>
            {discount && (
              <span className={s.priceNote}>
                <span className={s.struck}>{formatSum(listMonthly)}</span> · {discount}
              </span>
            )}
          </div>
          <div className={s.subChips}>
            <span className={`${s.subChip} ${sub.status === 'trial' ? s.chipMute : PAYMENT_TONE[sub.payment_status]}`}>
              {sub.status === 'trial' && sub.trial_until ? t('subscription.trial_until', { date: fullDate(sub.trial_until) }) : t(`subscription.payment.${sub.payment_status}`)}
            </span>
            <span className={`${s.subChip} ${s.chipDim}`}>{sub.ends_on ? `${fullDate(sub.starts_on)} – ${fullDate(sub.ends_on)}` : fullDate(sub.starts_on)}</span>
          </div>
        </Card>
        <Card padding="none" className={s.limits}>
          <div className={s.limitsTitle}>{t('subscription.limits')}</div>
          <div className={s.limitList}>
            {LIMITS.filter((k) => k in limits).map((k) => {
              const limit = limits[k];
              const used = usage[k];
              const known = typeof used === 'number';
              const value =
                k === 'storage_gb'
                  ? t('subscription.gb', { used: known ? formatNumber(used, Number.isInteger(used) ? 0 : 1) : tc('dash'), limit: limit ?? t('subscription.unlimited') })
                  : `${known ? formatNumber(used) : tc('dash')} / ${limit ?? t('subscription.unlimited')}`;
              // Whole percents, as the design's bars are drawn.
              const share = known && limit ? Math.round((used / limit) * 100) : 0;
              return (
                <div key={k} className={s.limit}>
                  <div className={s.limitRow}>
                    <span className={s.limitLabel}>{t(`subscription.limit.${k}`)}</span>
                    <span>{value}</span>
                  </div>
                  <Progress value={share} complete={share >= 100} label={t(`subscription.limit.${k}`)} />
                </div>
              );
            })}
          </div>
          <span className={s.limitsNote}>{t('subscription.note')}</span>
        </Card>
      </div>
      {canChange && (
        <Button variant="secondary" size={38} icon="pencil" iconSize={14} className={s.alignStart} onClick={() => setEditing(true)}>
          {t('subscription.manage')}
        </Button>
      )}
      {editing && <SubscriptionModal orgId={org.id} sub={sub} onClose={() => setEditing(false)} />}
    </>
  );
}

/**
 * Change the subscription: another plan creates a new subscription (POST, a new plan snapshot); the same
 * plan patches this one (PATCH). The individual price needs `subscription:override_price` (TZ §3, §5).
 */
function SubscriptionModal({ orgId, sub, onClose }: { orgId: string; sub: Subscription | null; onClose: () => void }) {
  const { t } = useTranslation('organizations');
  const { t: tc } = useTranslation('common');
  const { can } = useSession();
  const titleId = useModalTitleId();
  const toast = useToast();
  const qc = useQueryClient();
  const plans = useQuery(plansQuery).data ?? [];
  const [planId, setPlanId] = useState<string | null>(null);
  const plan = planId ?? sub?.plan_id ?? plans[0]?.id ?? '';
  const chosen = plans.find((p) => p.id === plan);
  const mayOverride = can('subscription:override_price');
  const [override, setOverride] = useState(sub?.override_price ? String(Math.round(num(sub.override_price))) : '');
  const [reason, setReason] = useState(sub?.discount_reason ?? '');
  const [contract, setContract] = useState(sub?.contract_ref ?? '');
  const [payment, setPayment] = useState<Schemas['PaymentStatus']>(sub?.payment_status ?? 'unknown');
  const [problem, setProblem] = useState<string | null>(null);
  // A plan that is no longer offered (an old version) keeps its own id: then only PATCH applies.
  const samePlan = !!sub && (plan === sub.plan_id || !chosen);

  const save = useMutation({
    mutationFn: async () => {
      const price = override.replace(/\s/g, '');
      const terms = { discount_reason: reason.trim() || null, contract_ref: contract.trim() || null, payment_status: payment };
      const priceField = mayOverride ? { override_price: price ? price : null } : {};
      if (sub && samePlan) return patchSubscription(sub.id, { ...terms, ...priceField });
      return assignPlan(orgId, { plan_id: plan, ...terms, ...(mayOverride && price ? { override_price: price } : {}) });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.orgSubscriptions(orgId) });
      void qc.invalidateQueries({ queryKey: qk.organization(orgId) });
      void qc.invalidateQueries({ queryKey: qk.subscriptions });
      void qc.invalidateQueries({ queryKey: qk.orgActivity(orgId) });
      toast(samePlan ? t('subscription.saved') : t('subscription.assigned', { plan: chosen?.name ?? '' }));
      onClose();
    },
    onError: (err) => setProblem(errorText(err, tc)),
  });

  return (
    <Modal open onClose={onClose} labelledBy={titleId}>
      <ModalHeader id={titleId} title={t('subscription.modal_title')} sub={t('subscription.modal_sub')} onClose={onClose} closeLabel={tc('actions.close')} />
      <div className={s.form}>
        <div className={s.group}>
          <span className={s.groupLabel}>{t('subscription.plan')}</span>
          <Segmented variant="form" label={t('subscription.plan')} value={plan} onChange={setPlanId} className={s.alignStart} options={plans.map((p) => ({ value: p.id, label: p.name }))} />
        </div>
        <div className={s.group}>
          <Input
            label={t('subscription.override')}
            inputMode="numeric"
            value={override}
            disabled={!mayOverride}
            placeholder={chosen ? formatSum(num(chosen.price)) : ''}
            onChange={(e) => setOverride(e.target.value)}
          />
          <FieldHint>{mayOverride ? t('subscription.override_hint', { price: chosen ? formatSum(num(chosen.price), true) : tc('dash') }) : t('subscription.override_denied')}</FieldHint>
        </div>
        <div className={s.grid2}>
          <Input label={t('subscription.reason')} value={reason} onChange={(e) => setReason(e.target.value)} />
          <Input label={t('subscription.contract_ref')} value={contract} onChange={(e) => setContract(e.target.value)} mono />
        </div>
        <div className={s.group}>
          <span className={s.groupLabel}>{t('subscription.payment_status')}</span>
          <Segmented
            variant="form"
            label={t('subscription.payment_status')}
            value={payment}
            onChange={setPayment}
            className={s.alignStart}
            options={(['unknown', 'awaiting', 'paid', 'overdue'] as const).map((k) => ({ value: k, label: t(`subscription.payment.${k}`) }))}
          />
        </div>
        {problem && (
          <span className={s.problem} role="alert">
            {problem}
          </span>
        )}
      </div>
      <ModalFooter>
        <Button size={46} variant="secondary" onClick={onClose}>
          {tc('actions.cancel')}
        </Button>
        <Button size={46} onClick={() => save.mutate()} disabled={save.isPending || !plan}>
          {t('subscription.save')}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
